import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Typography } from '@totalsoft/rocket-ui';
import ThinDivider from 'components/common/ThinDivider';
import moment from 'moment';
import { useQuery } from 'hooks/apollo';
import { CONTRACT_TYPES_DICTIONARY_QUERY } from 'features/financialProducts/list/queries';
import { CURRENCIES_DICTIONARY_QUERY, LEASING_INTEREST_TYPES_QUERY, SCHEDULE_TYPES_QUERY } from 'features/common/queries';
import { DOCUMENT_LEASING_INCOME_SCHEMAS_QUERY } from 'features/financialProducts/edit/queries';

export const RuleSummaryComponent = ({ productId, productSummary }) => {
  const { t } = useTranslation();

  const { data: contractTypesDictionaryData } = useQuery(CONTRACT_TYPES_DICTIONARY_QUERY);

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const { data: scheduleTypesData } = useQuery(SCHEDULE_TYPES_QUERY);

  const { data: leasingIncomeSchemasData } = useQuery(DOCUMENT_LEASING_INCOME_SCHEMAS_QUERY, {
    variables: { documentId: productId ?? null }
  });

  const { data: leasingInterestTypesData } = useQuery(LEASING_INTEREST_TYPES_QUERY, {
    variables: { currencyId: productSummary?.creditCurrencyId }
  });

  return (
    <Grid container spacing={1} alignItems='center' px={3} py={2}>
      <Grid item xs={12}>
        <Typography textTransform='uppercase' color='primary' fontWeight='bold' variant='subtitle1'>
          {t('FinancialProduct.Details.Rules.RightSidebar.SummaryProductTitle')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <ThinDivider />
      </Grid>
      {productSummary && (
        <>
          <Grid item xs={12} md={6}>
            {productSummary.validFrom && productSummary.validTo
              ? t('FinancialProduct.Details.Rules.RightSidebar.ValidityPeriod')
              : t('FinancialProduct.ValidFrom')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            {productSummary.validFrom && moment(productSummary.validFrom).format('DD.MM.YYYY')}
            {productSummary.validFrom && productSummary.validTo ? ' - ' : ''}
            {productSummary.validTo && moment(productSummary.validTo).format('DD.MM.YYYY')}
          </Grid>
          <Grid item xs={12} md={6}>
            {t('FinancialProduct.LeasingTypeName')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            {contractTypesDictionaryData?.contractTypesDictionary?.find(x => x.contractTypeId === productSummary?.leasingTypeId)
              ?.contractTypeName ?? ''}
          </Grid>
          <Grid item xs={12} md={6}>
            {t('FinancialProduct.Currency')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            {currenciesDictionaryData?.currenciesDictionary?.find(x => x.id === productSummary?.creditCurrencyId)?.code ?? ''}
          </Grid>
          {scheduleTypesData?.scheduleTypes?.find(x => x.scheduleTypeId === productSummary?.scheduleTypeId) && (
            <>
              <Grid item xs={12} md={6}>
                {t('FinancialProduct.PaymentPlanType')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                {scheduleTypesData?.scheduleTypes?.find(x => x.scheduleTypeId === productSummary?.scheduleTypeId)?.scheduleTypeName ?? ''}
              </Grid>
            </>
          )}
          {leasingIncomeSchemasData?.documentLeasingIncomeSchemas?.find(x => x.schemaId === productSummary?.leasingIncomeSchemaId) && (
            <>
              <Grid item xs={12} md={6}>
                {t('FinancialProduct.LeasingIncomeSchema')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                {leasingIncomeSchemasData?.documentLeasingIncomeSchemas?.find(x => x.schemaId === productSummary?.leasingIncomeSchemaId)
                  ?.name ?? ''}
              </Grid>
            </>
          )}
          {leasingInterestTypesData?.leasingInterestTypes?.find(x => x.interestTypeId === productSummary?.interestTypeId) && (
            <>
              <Grid item xs={12} md={6}>
                {t('FinancialProduct.LeasingInterestType')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                {leasingInterestTypesData?.leasingInterestTypes?.find(x => x.interestTypeId === productSummary?.interestTypeId)
                  ?.interestTypeName ?? ''}
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6}>
            {t('FinancialProduct.PaymentFrequency')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            {productSummary.paymentFrequency}
          </Grid>
          <Grid item xs={12} md={6}>
            {t('FinancialProduct.ContractDuration')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            {productSummary.contractDuration}
          </Grid>
          <Grid item xs={12}>
            <ThinDivider />
          </Grid>
        </>
      )}
    </Grid>
  );
};

RuleSummaryComponent.propTypes = {
  productId: PropTypes.number.isRequired,
  productSummary: PropTypes.object.isRequired
};

export default RuleSummaryComponent;
